import {
  type CSSProperties,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import type { NormalizedRect } from '../lib/imageContentBounds'
import { analyzePortraitFocusBounds } from '../lib/portraitFocusAnalyze'
import { portraitFocusFallbackClass, portraitFocusImgStyle } from '../lib/portraitFocusStyle'
import { referenceImageThumbnailUrl } from '../lib/referenceImageUrl'
import { ThemedImage } from './ThemedImage'

type Props = {
  src: string
  alt?: string
  frameClassName?: string
  compact?: boolean
  angular?: boolean
  className?: string
}

/** 角色卡竖版头像：Chrome/Edge FaceDetector 取景后铺满画框，否则 object-cover */
export function PortraitFocusFrame({
  src,
  alt = '',
  frameClassName = 'relative w-full overflow-hidden bg-surface-muted/50',
  compact,
  angular = false,
  className = '',
}: Props) {
  const displaySrc = useMemo(() => referenceImageThumbnailUrl(src, 360), [src])
  const [focusRect, setFocusRect] = useState<NormalizedRect | null>(null)
  const [analyzedSrc, setAnalyzedSrc] = useState<string | null>(null)

  useEffect(() => {
    setFocusRect(null)
    setAnalyzedSrc(null)
  }, [displaySrc])

  const onImageLoad = useCallback(
    async (e: SyntheticEvent<HTMLImageElement>) => {
      if (analyzedSrc === displaySrc) return
      setAnalyzedSrc(displaySrc)
      const bounds = await analyzePortraitFocusBounds(e.currentTarget)
      setFocusRect(bounds)
    },
    [analyzedSrc, displaySrc],
  )

  const imgStyle: CSSProperties | undefined = focusRect ? portraitFocusImgStyle(focusRect) : undefined
  const imgClass = focusRect
    ? 'absolute inset-0 z-[1]'
    : portraitFocusFallbackClass

  return (
    <div className={[frameClassName, className].filter(Boolean).join(' ')}>
      <ThemedImage
        src={displaySrc}
        alt={alt}
        compact={compact}
        angular={angular}
        className={imgClass}
        style={imgStyle}
        onLoad={onImageLoad}
      />
    </div>
  )
}
